import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/ui/DataTable';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import { formatDate } from '../../lib/utils';
import apiClient from '../../lib/api';
import { normalizeListPagination } from '../../lib/normalizeListPagination';
import { toast } from 'react-hot-toast';

const DesignersAdminPage = () => {
  const navigate = useNavigate();
  const [designers, setDesigners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeAccounts, setStripeAccounts] = useState({}); // Map of designerId -> account details
  const [companies, setCompanies] = useState({}); // Map of companyId -> company data

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Load designers data
  const loadDesigners = async (searchParams) => {
    try {
      setIsLoading(true);
      
      // Build API parameters
      const params = {
        page: searchParams?.page ?? pagination.page,
        limit: searchParams?.rowsPerPage ?? pagination.limit,
      };
      
      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }
      
      if (searchParams?.filterValues?.status) {
        params.status = searchParams.filterValues.status === 'true' ? 'active' : 'inactive';
      }
      
      // Call the API
      const response = await apiClient.getDesigners(params);
      
      const designersData = response.data || [];
      setDesigners(designersData);
      
      // Fetch company data for designers that have companyId
      const companiesMap = {};
      const uniqueCompanyIds = [...new Set(designersData.map(d => d.companyId).filter(Boolean))];
      
      await Promise.all(
        uniqueCompanyIds.map(async (companyId) => {
          try {
            const companyResponse = await apiClient.getCompany(companyId);
            companiesMap[companyId] = companyResponse.company || companyResponse.data || companyResponse;
          } catch (error) {
            console.error(`Error fetching company ${companyId}:`, error);
            companiesMap[companyId] = null;
          }
        })
      );
      setCompanies(companiesMap);
      
      // Fetch Stripe Connect account details using company's stripeConnectAccount ONLY
      // Do NOT use designer's stripeConnectAccount - it should only be on company
      const accountsMap = {};
      await Promise.all(
        designersData.map(async (designer) => {
          // Get company for this designer from the local map
          const company = companiesMap[designer.companyId];
          // ONLY use company's Stripe Connect account (no fallback to designer's)
          const stripeAccountId = company?.stripeConnectAccount;
          
          if (stripeAccountId) {
            try {
              const accountResponse = await apiClient.getStripeConnectAccount(stripeAccountId);
              accountsMap[designer.id] = accountResponse.data || accountResponse;
            } catch (error) {
              // Account might not be accessible, set to null
              accountsMap[designer.id] = null;
            }
          }
        })
      );
      setStripeAccounts(accountsMap);
            
      setPagination(normalizeListPagination(response, { limit: 10 }));
    } catch (error) {
      toast.error('Failed to load designers');
      setDesigners([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDesigners();
  }, []);

  const columns = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {`${row.firstName || ''} ${row.lastName || ''}`.trim() || 'Unnamed Designer'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.email || 'No email'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2">
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      sortable: true,
      render: (value, row) => (
        <Chip 
          label={row.isActive ? 'Active' : 'Inactive'} 
          size="small" 
          color={row.isActive ? 'success' : 'warning'}
          variant={row.isActive ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'createdAt',
      label: 'Joined',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      ),
    },
    {
      id: 'stripeConnect',
      label: 'Stripe Connect',
      sortable: false,
      render: (value, row) => {
        // Get company for this designer
        const company = companies[row.companyId];
        // ONLY use company's Stripe Connect account (no fallback to designer's)
        const stripeAccountId = company?.stripeConnectAccount;
        const account = stripeAccounts[row.id];
        
        if (!stripeAccountId) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not Connected
            </Typography>
          );
        }
        
        if (!account) {
          return (
            <Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {stripeAccountId.substring(0, 20)}...
              </Typography>
              <Chip label="Loading..." size="small" color="default" />
            </Box>
          );
        }
        
        const isVerified = account.details_submitted && account.charges_enabled && account.payouts_enabled;
        const status = isVerified ? 'Verified' : 'Pending';
        
        return (
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 0.5 }}>
              {stripeAccountId}
            </Typography>
            <Chip 
              label={status} 
              size="small" 
              color={isVerified ? 'success' : 'warning'}
            />
          </Box>
        );
      },
    },
  ];

  // Filter options
  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  const filters = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by name or email...'
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions,
    },
  ];

  // Handle search from DataTable
  const handleSearch = (searchParams) => {
    loadDesigners(searchParams);
  };

  const handleRowSelect = (designer) => {
    navigate(`/designers/${designer.id}`);
  };

  const handleAddDesigner = () => {
    navigate('/designers/add');
  };

  // Remove the full-page loading state that causes unmounting
  // Instead, show loading state within the DataTable component

  return (
    <DashboardLayout title="Designers Management">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Designers Management
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 2 
        }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PaletteIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Designers
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {pagination.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Active Designers
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {designers.filter(d => d.isActive).length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  New This Month
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {designers.filter(d => {
                    const created = new Date(d.createdAt);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Designers Table */}
      <Box sx={{ mt: 4 }}>         
        <DataTable
          data={designers}
          title="Designers"
          onAdd={handleAddDesigner}
          addButtonText="Add Designer"
          columns={columns}
          filters={filters}
          onSelect={handleRowSelect}
          onSearch={handleSearch}
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          loading={isLoading}
          onRowClick={(row) => navigate(`/designers/${row.id}`)}
        />
      </Box>
    </DashboardLayout>
  );
};

export default DesignersAdminPage;
